import { qk, STALE_TIME } from "@secretaa/sdk-react";
import { Button, Card, Dialog, EmptyState, Field } from "@secretaa/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../app/api.ts";
import { canManage, useCurrentWorkspace } from "../../app/session.ts";
import { errorMessage } from "../../shared/errors.ts";
import { Form, FormError, PageHeader } from "../../shared/form.tsx";

const TYPES = ["branch", "region", "department", "team"] as const;

interface Unit {
  id: string;
  parentId: string | null;
  type: string;
  name: string;
  archived: boolean;
  version: number;
}

// Cây đơn vị (F4b.5). Tuỳ chọn: workspace không có unit vẫn chạy (empty state, không bắt tạo).
export function OrgUnitsPage() {
  const { t } = useTranslation();
  const cur = useCurrentWorkspace()!;
  const qc = useQueryClient();
  const manage = canManage(cur.role);
  const units = useQuery({ queryKey: qk.orgUnits(cur.tenant), queryFn: () => api.orgUnits.list(cur.workspaceId), staleTime: STALE_TIME.workspaceMeta });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<(typeof TYPES)[number]>("branch");
  const [parent, setParent] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: qk.orgUnits(cur.tenant) });
  const create = useMutation({
    mutationFn: () => api.orgUnits.create(cur.workspaceId, { name, type, parentId: parent || null }),
    onSuccess: async () => {
      setOpen(false);
      setName("");
      await refresh();
    },
  });
  const archive = useMutation({ mutationFn: (u: Unit) => api.orgUnits.archive(cur.workspaceId, u.id, u.version), onSuccess: refresh });
  const all = (units.data ?? []) as Unit[];
  const children = (pid: string | null) => all.filter((u) => u.parentId === pid);
  const renderTree = (pid: string | null, depth: number) => (
    <ul className={depth ? "ml-5 border-l border-(--saa-border) pl-3" : ""}>
      {children(pid).map((u) => (
        <li key={u.id} className="py-1">
          <div className="flex items-center justify-between gap-2">
            <span className={u.archived ? "text-(--saa-fg-muted) line-through" : ""}>
              {u.name} <span className="text-xs text-(--saa-fg-muted)">· {t(`orgUnits.types.${u.type}`, { defaultValue: u.type })}</span>
              {u.archived ? <span className="ml-1 text-xs">({t("orgUnits.archived")})</span> : null}
            </span>
            {manage && !u.archived ? (
              <Button variant="ghost" className="h-7 px-2" onClick={() => window.confirm(t("orgUnits.archiveConfirm")) && archive.mutate(u)}>
                {t("common.archive")}
              </Button>
            ) : null}
          </div>
          {renderTree(u.id, depth + 1)}
        </li>
      ))}
    </ul>
  );
  return (
    <>
      <PageHeader title={t("orgUnits.title")} actions={manage ? <Button onClick={() => setOpen(true)}>{t("orgUnits.new")}</Button> : null} />
      <FormError message={archive.isError ? errorMessage(t, archive.error) : units.isError ? errorMessage(t, units.error) : null} />
      {units.isPending ? <p>{t("app.loading")}</p> : all.length === 0 ? <EmptyState title={t("orgUnits.empty")} /> : <Card>{renderTree(null, 0)}</Card>}
      <Dialog open={open} onOpenChange={setOpen} title={t("orgUnits.new")} closeLabel={t("common.close")}>
        <Form label={t("orgUnits.new")} onSubmit={() => create.mutate()}>
          <FormError message={create.isError ? errorMessage(t, create.error) : null} />
          <Field label={t("orgUnits.fields.name")} required value={name} onChange={(e) => setName(e.target.value)} />
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("orgUnits.fields.type")}
            <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={type} onChange={(e) => setType(e.target.value as (typeof TYPES)[number])}>
              {TYPES.map((x) => (
                <option key={x} value={x}>
                  {t(`orgUnits.types.${x}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5 text-sm font-medium">
            {t("orgUnits.fields.parent")}
            <select className="h-9 rounded-md border border-(--saa-border) bg-(--saa-surface) px-2 font-normal" value={parent} onChange={(e) => setParent(e.target.value)}>
              <option value="">{t("orgUnits.root")}</option>
              {all
                .filter((u) => !u.archived)
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
            </select>
          </label>
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            {t("common.create")}
          </Button>
        </Form>
      </Dialog>
    </>
  );
}
